// src/pages/admin/AuditLogPage.js
// Updated: 11/10/2025 06:21 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { collection, query, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { ListFilter, FileClock, ChevronDown, ChevronUp, User, File, Vote, Award, UserCheck, Shield, Key, Lock, Eye } from 'lucide-react';

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

// Helper to format Firestore Timestamps
const formatTimestamp = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  return 'Invalid Date';
};

const AUDIT_ACTIONS = [
  { key: "USER_CREATED", label: "User Created" },
  { key: "USER_ROLE_CHANGED", label: "User Role Changed" },
  { key: "USER_MANUALLY_VERIFIED", label: "User Manually Verified" },
  { key: "DOCUMENT_UPLOADED", label: "Document Uploaded" },
  { key: "DOCUMENT_DELETED", label: "Document Deleted" },
  { key: "MOTION_CREATED", label: "Motion Created" },
  { key: "MOTION_STATUS_CHANGED", label: "Motion Status Changed" },
  { key: "MOTION_VISIBILITY_CHANGED", label: "Motion Visibility Changed" },
  { key: "MOTION_VOTE_CAST", label: "Motion Vote Cast" },
  { key: "SESSION_STATUS_CHANGED", label: "Session Status Changed" },
  { key: "REVIEWER_ASSIGNED", label: "Reviewer Assigned" },
  { key: "REVIEWER_REMOVED", label: "Reviewer Removed" },
  { key: "SPONSOR_ASSIGNED", label: "Sponsor Assigned" },
  { key: "SPONSOR_REMOVED", label: "Sponsor Removed" },
  { key: "SCORE_SUBMITTED", label: "Score Submitted" },
  { key: "SCORE_UPDATED", label: "Score Updated" },
  { key: "IMPERSONATION_STARTED", label: "Impersonation Started" },
  { key: "IMPERSONATION_STOPPED", label: "Impersonation Stopped" },
];


// Human-readable details formatter component
const FormatLogDetails = ({ log }) => {
  const { details } = log;
  if (!details) return <span className="text-gray-400">N/A</span>;

  const Detail = ({ icon: Icon, children }) => (
    <div className="flex items-center space-x-2">
      <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
      <span className="text-gray-700">{children}</span>
    </div>
  );

  switch (log.action) {
    case "USER_ROLE_CHANGED":
      return <Detail icon={User}>Changed role from <strong>{details.from}</strong> to <strong>{details.to}</strong> for {details.targetEmail}</Detail>;
      
    case "USER_STATUS_CHANGED": // <--- ADDED/REFINED CASE
      const actionVerb = details.to === 'inactive' ? 'Inactivated' : 'Reactivated';
      return <Detail icon={User}>
        <strong>{actionVerb}</strong> user <strong>{details.name}</strong>. Status changed from 
        <span className="ml-1 text-xs px-1 rounded bg-gray-200"> {details.from} </span> to 
        <span className="ml-1 text-xs px-1 rounded bg-gray-200"> {details.to} </span>
      </Detail>;
      
    case "USER_MANUALLY_VERIFIED":
      return <Detail icon={UserCheck}>Manually verified email for <strong>{details.targetEmail}</strong></Detail>;

    case "DOCUMENT_DELETED":
    case "DOCUMENT_UPLOADED":
      return <Detail icon={File}>Title: <strong>{details.title || details.fileName}</strong></Detail>;

    case "MOTION_VOTE_CAST":
      return <Detail icon={Vote}>Voted: <strong>{details.voteChoice}</strong> for "{details.motionTitle}"</Detail>;
    case "MOTION_CREATED":
      return <Detail icon={Vote}>Created motion: <strong>{details.title}</strong> (Type: {details.voteType})</Detail>;
    case "MOTION_STATUS_CHANGED":
      return <Detail icon={Lock}>Changed status from <strong>{details.from}</strong> to <strong>{details.to}</strong> for "{details.motionTitle}"</Detail>;
    case "MOTION_VISIBILITY_CHANGED":
      return <Detail icon={Eye}>Set results visibility to <strong>{details.public ? 'Public' : 'Private'}</strong> for "{details.motionTitle}"</Detail>;

    case "SCORE_SUBMITTED":
    case "SCORE_UPDATED":
      return <Detail icon={Award}>Score: <strong>{details.totalWeightedScore.toFixed(2)}</strong> for "{details.packageName}"</Detail>;

    case "SESSION_STATUS_CHANGED":
      return <Detail icon={Key}>Changed status from <strong>{details.from}</strong> to <strong>{details.to}</strong> for "{details.sessionName}"</Detail>;
      
    case "REVIEWER_ASSIGNED":
    case "SPONSOR_ASSIGNED":
      return <Detail icon={UserCheck}>Assigned <strong>{details.name}</strong> to "{details.sessionName}"</Detail>;
    case "REVIEWER_REMOVED":
    case "SPONSOR_REMOVED":
      return <Detail icon={UserCheck}>Removed <strong>{details.name}</strong> from "{details.sessionName}"</Detail>;

    case "IMPERSONATION_STARTED":
      return <Detail icon={Shield}>Started impersonating <strong>{details.targetEmail}</strong></Detail>;
    case "IMPERSONATION_STOPPED":
      return <Detail icon={Shield}>Stopped impersonating</Detail>;
    case "USER_CREATED":
      return <Detail icon={User}>New user <strong>{details.name}</strong> ({details.email}) joined as {details.role}</Detail>;
    default:
      return <pre className="overflow-x-auto whitespace-pre-wrap">{JSON.stringify(details, null, 2)}</pre>;
  }
};

// Human-readable formatter for the Target column
const FormatLogTarget = ({ log }) => {
  const { targetType, targetId, details } = log;
  let title = '';
  let subtext = targetId;

  switch (targetType) {
    case 'user':
      title = details?.name || details?.targetEmail || 'User';
      break;
    case 'scoringSession':
      title = details?.sessionName || 'Scoring Session';
      break;
    case 'scoringPackage':
      title = details?.packageName || 'Scoring Package';
      subtext = details?.sessionId || targetId;
      break;
    case 'motion':
      title = details?.motionTitle || 'Motion';
      break;
    case 'document':
      title = details?.title || details?.fileName || 'Document';
      break;
    default:
      title = targetType;
  }

  if (title === subtext) {
    subtext = null;
  }

  return (
    <div>
      <span className="font-medium text-gray-900">{title}</span>
      {subtext && <span className="block text-xs text-gray-500 truncate" title={subtext}>{subtext}</span>}
    </div>
  );
};


export default function AuditLogPage() {
  const { userData, isAdmin, isLoading: isAuthLoading } = useAuth();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterTerm, setFilterTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Fetch logs (No changes)
  useEffect(() => {
    if (isAuthLoading || !userData?.orgId || !isAdmin) {
      if (!isAuthLoading && !isAdmin) {
        setError('Access Denied. You must be an admin to view this page.');
      }
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const logsQuery = query(
      collection(db, 'organizations', userData.orgId, 'auditLog'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(
      logsQuery,
      (querySnapshot) => {
        const logsData = [];
        querySnapshot.forEach((doc) => {
          logsData.push({ id: doc.id, ...doc.data() });
        });
        setLogs(logsData);
        setIsLoading(false);
        setError('');
      },
      (err) => {
        console.error('Error fetching audit logs:', err);
        setError('Failed to load audit logs. Check Firestore rules.');
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [userData?.orgId, isAdmin, isAuthLoading]);

  // Client-side filtering and sorting
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = [...logs];
    if (userData && !userData.super_admin) {
      filtered = filtered.filter(log =>
        log.action !== 'IMPERSONATION_STARTED' &&
        log.action !== 'IMPERSONATION_STOPPED'
      );
    }
    const start = startDate ? new Date(startDate) : null;
    if (start) start.setHours(0, 0, 0, 0);
    const end = endDate ? new Date(endDate) : null;
    if (end) end.setHours(23, 59, 59, 999);
    if (filterTerm || actionFilter || start || end) {
      const lowerFilter = filterTerm.toLowerCase();
      filtered = filtered.filter(log => {
        if (filterTerm) {
          const textMatch =
            log.actorName.toLowerCase().includes(lowerFilter) ||
            log.action.toLowerCase().includes(lowerFilter) ||
            log.targetType.toLowerCase().includes(lowerFilter) ||
            log.targetId.toLowerCase().includes(lowerFilter) ||
            JSON.stringify(log.details).toLowerCase().includes(lowerFilter);
          if (!textMatch) return false;
        }
        if (actionFilter && log.action !== actionFilter) {
          return false;
        }
        if (log.timestamp) {
          const logDate = log.timestamp.toDate();
          if (start && logDate < start) return false;
          if (end && logDate > end) return false;
        } else {
          if (start || end) return false;
        }
        return true;
      });
    }
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (aVal instanceof Timestamp) aVal = aVal.toMillis();
        if (bVal instanceof Timestamp) bVal = bVal.toMillis();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [logs, filterTerm, actionFilter, startDate, endDate, sortConfig, userData]);

  // Table header sort handlers
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="inline h-4 w-4" /> : <ChevronDown className="inline h-4 w-4" />;
  };

  if (isLoading || isAuthLoading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
        <FileClock className="mr-3 h-8 w-8 text-indigo-600" />
        Organization Audit Log
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        An immutable, server-generated log of important actions within your organization.
      </p>

      {/* Filter Input */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Filter by actor, action, target, or details..."
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
          className="w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">
          <ListFilter className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Filter UI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label htmlFor="action-filter" className="block text-sm font-medium text-gray-700">
            Action Type
          </label>
          <select
            id="action-filter"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All Actions</option>
            {AUDIT_ACTIONS.map(action => (
              <option key={action.key} value={action.key}>{action.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">
            End Date
          </label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Log Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('timestamp')}
              >
                Timestamp {getSortIcon('timestamp')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('actorName')}
              >
                Actor {getSortIcon('actorName')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('action')}
              >
                Action {getSortIcon('action')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer"
                onClick={() => requestSort('targetType')}
              >
                Target {getSortIcon('targetType')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredAndSortedLogs.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  {isLoading ? 'Loading logs...' : 'No logs found matching your filter.'}
                </td>
              </tr>
            ) : (
              filteredAndSortedLogs.map((log) => (
                <tr key={log.id}>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {log.actorName} {log.actorUid === 'system' && '(System)'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-mono text-blue-600">
                    {log.action}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    <FormatLogTarget log={log} />
                  </td>
                  <td className="px-6 py-4 text-sm" style={{ minWidth: '300px' }}>
                    <FormatLogDetails log={log} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}